import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';
import { TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './SelfBillingFeeTermsWidget.module.scss';
import { InfoBox } from '../../../shared/components/ui-components';
import { ModeButtonsBlock } from '../../../shared/components/buttons';
import { FeeTermsInfo, FeeTermsFormValues } from '../../interfaces/feeTerms';
import { useMode } from '../../../shared/hooks/useMode';
import { entityGeneralSetupService } from '../../services/entityGeneralSetupService';
import { ClientFeeInput } from './ClientFeeInput/ClientFeeInput';
import { SaasTaxInput } from './SaasTaxInput/SaasTaxInput';
import { Mode } from '../../../general-types';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';

type SelfBillingFeeTermsWidgetProps = {
  entityId: number;
  isFeeTermsCreated: boolean;
  feeTerms?: FeeTermsInfo;
  defaultMode?: Mode;
  onSuccessSubmit?: () => void;
};

const SelfBillingFeeTermsWidget = observer(
  ({
    entityId,
    isFeeTermsCreated,
    feeTerms,
    defaultMode = 'EDIT',
    onSuccessSubmit = () => {},
  }: SelfBillingFeeTermsWidgetProps) => {
    const { isSuperAdmin } = loggedInUserDataStore;

    const { mode, setEdit, setView } = useMode(isSuperAdmin ? defaultMode : 'VIEW');

    const { control, reset, handleSubmit, getValues } = useForm<FeeTermsFormValues>();

    useEffect(() => {
      if (feeTerms) {
        reset({
          activeClientFee: feeTerms?.activeClientFee,
          saasTax: feeTerms?.saasTax,
        });
      }
    }, [feeTerms]);

    const onSubmit = handleSubmit(async (data: FeeTermsFormValues) => {
      if (isFeeTermsCreated) {
        await entityGeneralSetupService.updateFeeTerms(entityId, data);
      } else {
        await entityGeneralSetupService.createFeeTerms(entityId, data);
      }

      reset(data);
      onSuccessSubmit();
      setView();
    });

    const handleCancel = () => {
      reset();

      setView();
    };

    const ManageButtons = (
      <ModeButtonsBlock
        mode={mode}
        onEdit={setEdit}
        onCancel={handleCancel}
        onSubmit={onSubmit}
      />
    );

    return (
      <InfoBox title="Fee Terms" icon={isSuperAdmin && ManageButtons}>
        <div className={classes.container}>
          <div className={classes.descriptionBlock}>
            <TitleWithDescription
              title="LifeBase Fees"
              description="The entity will be automatically debited on a monthly basis"
              titleFont="body-14-bold"
            />
          </div>
          <div className={classes.feeTermsBlock}>
            <TitleWithDescription
              title="Active Client Fee"
              description="An entity will take payment from nutrition clients however they choose; i.e. cash, or other payment processor"
              titleFont="body-14-bold"
            />
            <ClientFeeInput
              control={control}
              mode={mode}
              clientFee={getValues().activeClientFee}
              countOfClients={feeTerms?.activeUsers}
            />
            <TitleWithDescription
              title="Sales Tax for LifeBase services"
              description={
                <>
                  Set up the combined SaaS tax, and sales tax percentages, based on the
                  Entity&apos;s State/City taxes.
                  <br />
                  The tax amount is calculated based on the entity billing address
                </>
              }
              titleFont="body-14-bold"
            />
            <SaasTaxInput mode={mode} control={control} saasTax={getValues().saasTax} />
          </div>
        </div>
      </InfoBox>
    );
  },
);

export { SelfBillingFeeTermsWidget };
